import type { TranslationKey } from "../../../state/i18n";
import type { Query } from "../../../services/ApiService";
import type { GenericSchema } from "valibot";
import { decimalValue, requireText } from "../../../utils/validation/base";
import { MAX_CHARACTERS_XL } from "../../../constants";
import { escapeRegex } from "../../../utils/regexUtil";
import { keyFromSelector } from "i18next";

export type Filter = {
  id: string;
  type: FilterType;
  path: string;
  operator: FilterOperator;
  value: string;
};

export type FilterType = "string" | "number" | "boolean" | "date" | "dateString" | "none";
export type FilterOperator =
  | "isFilledIn"
  | "is"
  | "isNot"
  | "contains"
  | "startsWith"
  | "endsWith"
  | "greaterThan"
  | "lessThan"
  | "isBefore"
  | "isAfter";

type InputType = "text" | "number" | "select" | "date" | "time" | "datetime";
export type FilterDefinition = {
  operator: FilterOperator;
  inputType: InputType;
  schema?: GenericSchema;
} & (
  | { inputType: "text"; schema: GenericSchema<string> }
  | { inputType: "number"; allowDecimal?: boolean; schema: GenericSchema<number> }
  | {
      inputType: "select";
      placeholderLabel?: TranslationKey;
      defaultValue?: string;
      options: {
        id: string;
        label: TranslationKey;
      }[];
    }
  | { inputType: "date" | "time" | "datetime" }
);

export const isFilledInFilter: FilterDefinition = {
  operator: "isFilledIn",
  inputType: "select",
  options: [
    { id: "true", label: keyFromSelector(($) => $.TABLE_FILTER_ALL_FILLED_IN_OPTIONS_YES_LABEL) },
    { id: "false", label: keyFromSelector(($) => $.TABLE_FILTER_ALL_FILLED_IN_OPTIONS_NO_LABEL) },
  ],
};
export const defaultStringFilter: FilterDefinition[] = [
  {
    operator: "is",
    inputType: "text",
    schema: requireText(MAX_CHARACTERS_XL),
  },
  {
    operator: "isNot",
    inputType: "text",
    schema: requireText(MAX_CHARACTERS_XL),
  },
  {
    operator: "contains",
    inputType: "text",
    schema: requireText(MAX_CHARACTERS_XL),
  },
  {
    operator: "startsWith",
    inputType: "text",
    schema: requireText(MAX_CHARACTERS_XL),
  },
  {
    operator: "endsWith",
    inputType: "text",
    schema: requireText(MAX_CHARACTERS_XL),
  },
  isFilledInFilter,
];
export const defaultNumberFilter: FilterDefinition[] = [
  { operator: "is", inputType: "number", schema: decimalValue(-MAX_CHARACTERS_XL, MAX_CHARACTERS_XL) },
  { operator: "isNot", inputType: "number", schema: decimalValue(-MAX_CHARACTERS_XL, MAX_CHARACTERS_XL) },
  {
    operator: "greaterThan",
    inputType: "number",
    schema: decimalValue(-MAX_CHARACTERS_XL, MAX_CHARACTERS_XL),
  },
  {
    operator: "lessThan",
    inputType: "number",
    schema: decimalValue(-MAX_CHARACTERS_XL, MAX_CHARACTERS_XL),
  },
  isFilledInFilter,
];

export const toApi = (filter: Filter): Query => {
  if (filter.operator === "isFilledIn") {
    return {
      path: filter.path,
      type: "exists",
      value: filter.value === "true",
    };
  }

  if (filter.type === "boolean") {
    if (filter.operator === "is") {
      return {
        path: filter.path,
        type: "boolean",
        value: filter.value === "true",
      };
    }
  } else if (filter.type === "string") {
    const escapedFilterValue = escapeRegex(filter.value);
    let value;
    if (filter.operator === "is") {
      value = `^${escapedFilterValue}$`;
    } else if (filter.operator === "isNot") {
      value = `^(?!${escapedFilterValue}$).*`;
    } else if (filter.operator === "contains") {
      value = escapedFilterValue;
    } else if (filter.operator === "startsWith") {
      value = `^${escapedFilterValue}.*$`;
    } else if (filter.operator === "endsWith") {
      value = `^.*${escapedFilterValue}$`;
    } else {
      throw new Error(`Unsupported operator "${filter.operator}"`);
    }

    return {
      path: filter.path,
      type: "string",
      value: value,
    };
  } else if (filter.type === "number") {
    let match;
    if (filter.operator === "is") {
      match = "eq" as const;
    } else if (filter.operator === "isNot") {
      match = "neq" as const;
    } else if (filter.operator === "greaterThan") {
      match = "gt" as const;
    } else if (filter.operator === "lessThan") {
      match = "lt" as const;
    } else {
      throw new Error(`Unsupported operator "${filter.operator}"`);
    }

    return {
      path: filter.path,
      type: "number",
      value: {
        match: match,
        value: parseFloat(filter.value),
      },
    };
  } else if (filter.type === "dateString") {
    if (filter.operator === "is") {
      return {
        path: filter.path,
        type: "string",
        value: filter.value,
      };
    } else if (filter.operator === "isBefore") {
      return {
        path: filter.path,
        type: "date",
        value: {
          end: filter.value,
        },
      };
    } else if (filter.operator === "isAfter") {
      return {
        path: filter.path,
        type: "date",
        value: {
          start: filter.value,
        },
      };
    }
  } else if (filter.type === "date") {
    if (filter.operator === "is") {
      const start = new Date(filter.value);
      const end = new Date(filter.value);
      end.setMinutes(end.getMinutes() + 1); // So we get everything up to the next minute

      return {
        path: filter.path,
        type: "date",
        value: {
          start: start.getTime(),
          end: end.getTime(),
        },
      };
    } else if (filter.operator === "isBefore") {
      return {
        path: filter.path,
        type: "date",
        value: {
          end: new Date(filter.value).getTime(),
        },
      };
    } else if (filter.operator === "isAfter") {
      return {
        path: filter.path,
        type: "date",
        value: {
          start: new Date(filter.value).getTime(),
        },
      };
    }
  }

  throw new Error("Invalid filter");
};
